import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchSuggestions, useCategories } from './catalogApi';
import { formatInr, retailPrice } from './retailUtils';
import { SearchIcon } from './Icons';

const DEBOUNCE_MS = 200;

/** Search with instant suggestions, as on the retail store. The catalogue is too big to search
 *  in the browser, so suggestions come from the server once typing pauses. Enter / "See all"
 *  goes to the full results list. */
export default function SearchBox({ autoFocus = false, onDone }) {
  const navigate = useNavigate();
  const categories = useCategories();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [items, setItems] = useState([]);
  const [searched, setSearched] = useState('');
  const blurTimer = useRef(null);

  const query = q.trim().toLowerCase();
  useEffect(() => {
    if (query.length < 2) { setItems([]); setSearched(''); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      searchSuggestions(q.trim())
        .then(list => { if (alive) { setItems(list.filter(p => p.itemsAvailable !== 0).slice(0, 6)); setSearched(query); } })
        .catch(() => alive && setItems([]));
    }, DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(t); };
  }, [q, query]);
  const matchingCategories = useMemo(() => (query.length < 2 ? []
    : categories.map(c => c.name).filter(c => c.toLowerCase().includes(query)).slice(0, 3)), [categories, query]);

  const go = (path) => { setOpen(false); setQ(''); onDone?.(); navigate(path); };
  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && items[active]) return go(`/product/${items[active].id}`);
    if (query) go(`/catalog?q=${encodeURIComponent(q.trim())}`);
  };
  const showList = open && query.length >= 2;
  const settled = searched === query;

  return (
    <form className="rt-searchbox" role="search" onSubmit={submit}>
      <SearchIcon size={18} />
      <input
        type="search" className="rt-input" placeholder="Search for products, brands and more"
        aria-label="Search products" autoFocus={autoFocus} value={q}
        role="combobox" aria-expanded={showList} aria-controls="rt-search-list" aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `rt-sugg-${active}` : undefined}
        onChange={e => { setQ(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(-1, a - 1)); }
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      {showList && (
        <div className="rt-suggest" id="rt-search-list" role="listbox" onMouseDown={() => clearTimeout(blurTimer.current)}>
          {matchingCategories.map(c => (
            <button key={c} type="button" className="rt-suggest-cat" onClick={() => go(`/catalog?category=${encodeURIComponent(c)}`)}>
              <SearchIcon size={14} /> {c}
            </button>
          ))}
          {items.map((p, i) => (
            <button key={p.id} id={`rt-sugg-${i}`} type="button" role="option" aria-selected={i === active}
              className={`rt-suggest-item${i === active ? ' is-active' : ''}`} onClick={() => go(`/product/${p.id}`)}>
              {p.thumbnailUrl ? <img src={p.thumbnailUrl} alt="" /> : <span />}
              <span className="rt-suggest-name">{p.name}</span>
              <span className="rt-suggest-price">{formatInr(retailPrice(p).price)}</span>
            </button>
          ))}
          {settled && items.length === 0 && matchingCategories.length === 0 && <div className="rt-suggest-empty">No matches for “{q.trim()}”</div>}
          {!settled && items.length === 0 && <div className="rt-suggest-empty">Searching…</div>}
          {items.length > 0 && (
            <button type="submit" className="rt-suggest-all">See all results for “{q.trim()}”</button>
          )}
        </div>
      )}
    </form>
  );
}
